import { observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { MailingInputGroup } from './mailing-input-group';

// ==============================================================================
// TYPE DEFINITIONS
// ==============================================================================

export type mailingFormUserInfo = {
    firstName: string;
    zipCode: string;
    email: string;
};

type validationField = {
    value: string;
    label: string;
    isValid?: boolean;
    errorMessage?: string;
    beenBlurred?: boolean;
};

interface IMailingFormComponentProps {
    onSubmit(userInfo: mailingFormUserInfo): unknown;
}

// ==============================================================================
// CLASS DEFINITIONS
// ==============================================================================

/**
 * component responsible for gathering user information for mailing
 */
@observer
export class MailingFormComponent extends React.Component<IMailingFormComponentProps> {

    // ==========================================================================
    // FIELDS
    // ==========================================================================

    @observable private _firstName: validationField = {
        label: 'First Name*',
        value: ''
    };

    @observable private _zipCode: validationField = {
        label: 'Zip Code*',
        value: ''
    };
    @observable private _email: validationField = {
        label: 'Email*',
        value: ''
    };

    private _emailRegex: RegExp = /^[-a-z0-9!#$%&'*+/=?^_`{|}~]+(?:\.[-a-z0-9!#$%&'*+/=?^_`{|}~]+)*@(?:[a-z0-9]+(?:-+[a-z0-9]+)*\.)+(?:xn--[a-z0-9]+|[a-z]{2,16})$/i;
    private _zipCodeRegex: RegExp = /^[0-9]{5}(?:-[0-9]{4})?$/;

    // ==========================================================================
    // PUBLIC METHODS
    // ==========================================================================

    public constructor(props: IMailingFormComponentProps) {
        super(props);

        this._onSubmitForm = this._onSubmitForm.bind(this);

        this._onFirstNameChange = this._onFirstNameChange.bind(this);
        this._onZipCodeChange = this._onZipCodeChange.bind(this);
        this._onEmailChange = this._onEmailChange.bind(this);
        this._lostFocus = this._lostFocus.bind(this);
    }

    public render(): JSX.Element {

        return (
            <div>
                <MailingInputGroup
                    initialValue={this._firstName.value}
                    label={this._firstName.label}
                    isValid={this._firstName.isValid}
                    validationMessage={this._firstName.errorMessage}
                    onValueChange={this._onFirstNameChange}
                    onLoseFirstFocus={this._lostFocus}
                />
                <MailingInputGroup
                    initialValue={this._zipCode.value}
                    label={this._zipCode.label}
                    isValid={this._zipCode.isValid}
                    validationMessage={this._zipCode.errorMessage}
                    onValueChange={this._onZipCodeChange}
                    onLoseFirstFocus={this._lostFocus}
                />
                <MailingInputGroup
                    initialValue={this._email.value}
                    label={this._email.label}
                    isValid={this._email.isValid}
                    validationMessage={this._email.errorMessage}
                    onValueChange={this._onEmailChange}
                    onLoseFirstFocus={this._lostFocus}
                />
                <button
                    className={
                        `btn btn-primary ${this._isButtonDisabled() ? 'disabled' : ''}`
                    }
                    onClick={this._onSubmitForm}
                    disabled={this._isButtonDisabled()}
                >
                    SUBMIT
                </button>
            </div>
        );
    }

    // ==========================================================================
    // COMPONENT EVENTS
    //
    // ALL EVENTS ARE ALSO TIED TO THEIR RESPECTIVE VALIDATORS
    // ==========================================================================

    private _onFirstNameChange(name: string): void {
        this._firstName.value = name;
        if (!this._firstName.beenBlurred) { return; }
        this._testForRequirement(this._firstName, 'Please enter your first name.');
    }

    private _onZipCodeChange(code: string): void {
        this._zipCode.value = code;

        if (!this._zipCode.beenBlurred) { return; }
        this._testForRequirement(this._zipCode, 'Please enter your zip code.');
        if (this._zipCode.isValid) {
            this._testForRegex(this._zipCode, this._zipCodeRegex, 'Please enter a valid Zip Code.');
        }
    }
    private _onEmailChange(email: string): void {
        this._email.value = email;

        if (!this._email.beenBlurred) { return; }
        this._testForRequirement(this._email, 'Please enter your email address.');
        if (this._email.isValid) {
            this._testForRegex(this._email, this._emailRegex, 'Please enter a valid email address.');
        }
    }

    // ==========================================================================
    // PRIVATE METHODS
    // ==========================================================================

    /**
     * determines if the button is conditionally disabled currently
     */
    private _isButtonDisabled(): boolean {

        // button will only be disabled if all input fields are considered invalid
        // or undefined (meaning not filled out or touched)
        return !(this._firstName.isValid && this._zipCode.isValid && this._email.isValid);
    }

    /**
     * this method checks which field was blurred and sets the beenBlurred
     * boolean to true, meaning that it was noticed by the user and can now show
     * error messages
     *
     * @param label the label of the field that has lost its focus
     */
    private _lostFocus(label: string): unknown {

        // also run all change events related to the field so that the proper
        // errors propogate on blur
        switch (label) {
            case this._firstName.label:
                this._firstName.beenBlurred = true;
                return this._onFirstNameChange(this._firstName.value);
            case this._zipCode.label:
                this._zipCode.beenBlurred = true;
                return this._onZipCodeChange(this._zipCode.value);
            case this._email.label:
                this._email.beenBlurred = true;
                return this._onEmailChange(this._email.value);
            default:
                return console.warn('[mailing-form:MailingFormComponent._lostFocus] failed to link a field');
        }
    }

    /**
     * method responsible for sending all values submitted to the parent
     */
    private _onSubmitForm(): void {
        this.props.onSubmit({
            email: this._email.value,
            firstName: this._firstName.value,
            zipCode: this._zipCode.value
        });
    }

    // ==============================================================================
    // VALIDATION METHODS
    // ==============================================================================

    private _testForRequirement(field: validationField, errorMessage: string): void {
        if (field.value.length === 0) {
            field.isValid = false;
            field.errorMessage = errorMessage;
        } else {
            field.isValid = true;
        }

        // hack for not re-rendering field value for first time after blur for some
        // reason
        this.setState({});
    }

    private _testForRegex(field: validationField, regex: RegExp, errorMessage: string): void {
        if (regex.test(field.value)) {
            field.isValid = true;
        } else {
            field.isValid = false;
            field.errorMessage = errorMessage;
        }
    }
}